// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Anmol Rajpal <anmol@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

// Structured, PHI-free diagnostics for the Vapi voice lifecycle. Prefixed "[vapi-intake]"
// so it filters cleanly in the browser console, and it NEVER logs transcript text or any
// PHI — only event names, Vapi endedReason, boolean/numeric metadata, and client-perceived
// latencies/gaps. Every line carries the opaque conversationId + Vapi callId + assistantId
// so a live session joins to backend logs and pinpoints WHY a call dropped from the client.
//
// Enabled when running the local dev server (import.meta.env.DEV) OR when a tester opts in
// on a deployed build via `?voiceDiag=1` in the URL (persisted to localStorage), so a live
// pilot session on the dev host can be captured without emitting anything for real patients.

const DIAG_QUERY_PARAM = 'voiceDiag'
const DIAG_STORAGE_KEY = 'qh:voiceDiag'
const PREFIX = '[vapi-intake]'

type LogFields = Record<string, string | number | boolean | undefined>

// Runtime opt-in: `?voiceDiag=1` turns diagnostics on (and `=0` off) for the browser,
// persisting the choice so it survives the in-app navigations during a single session.
function runtimeOptIn(): boolean {
  if (typeof window === 'undefined') return false
  try {
    const param = new URL(window.location.href).searchParams.get(DIAG_QUERY_PARAM)
    if (param === '1' || param === 'true') {
      window.localStorage.setItem(DIAG_STORAGE_KEY, '1')
      return true
    }
    if (param === '0' || param === 'false') {
      window.localStorage.removeItem(DIAG_STORAGE_KEY)
      return false
    }
    return window.localStorage.getItem(DIAG_STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

function isEnabled(): boolean {
  return import.meta.env.DEV || runtimeOptIn()
}

/** Opaque, non-PHI identifiers that join a client line to the backend + Vapi records. */
export interface VoiceLogContext {
  conversationId?: string
  assistantId?: string
  callId?: string
}

/**
 * Per-call logger. Carries the call context (conversationId/assistantId/callId) into every
 * line, and tracks two latencies that explain a drop: the reply round-trip the patient feels
 * (patient speech-end → assistant speech-start), and the gap since the assistant was last
 * active (so a mid-call stall — the 20s+ silent tail before a drop — shows as a large gap on
 * the error/call-end line).
 */
export function createVoiceLogger(context: VoiceLogContext = {}) {
  const ctx: VoiceLogContext = { ...context }
  let patientSpeechEndedAt: number | null = null
  let lastAssistantActivityAt: number | null = null
  let firstAssistantSpoke = false

  function emit(event: string, fields?: LogFields): void {
    if (!isEnabled()) return
    const base: LogFields = {
      at: new Date().toISOString(),
      tMs: Math.round(performance.now()),
      conversationId: ctx.conversationId,
      assistantId: ctx.assistantId,
      callId: ctx.callId,
    }
    const line: LogFields = { ...base, ...fields }
    for (const k of Object.keys(line)) if (line[k] === undefined) delete line[k]
    console.info(`${PREFIX} ${event}`, line)
  }

  // ms since the assistant was last active (spoke or produced a final turn), or undefined
  // if it never has. Large values right before an error/end reveal a stall-then-drop.
  function gapSinceLastAssistant(): number | undefined {
    if (lastAssistantActivityAt === null) return undefined
    return Math.round(performance.now() - lastAssistantActivityAt)
  }

  return {
    log: emit,
    /** Attach the Vapi callId (known only once start() resolves) to all subsequent lines. */
    setCallId(callId: string | undefined): void {
      if (callId) ctx.callId = callId
    },
    /** Note assistant activity (speech or a final turn) — anchors the inter-turn gap clock. */
    markAssistantActivity(): void {
      lastAssistantActivityAt = performance.now()
    },
    /** Patient finished a turn — start the reply-latency clock. */
    markPatientSpeechEnd(): void {
      patientSpeechEndedAt = performance.now()
    },
    /** Assistant began speaking — report reply latency + first-audio, and log the event. */
    markAssistantSpeechStart(): void {
      const fields: LogFields = {}
      if (patientSpeechEndedAt !== null) {
        fields.replyLatencyMs = Math.round(performance.now() - patientSpeechEndedAt)
        patientSpeechEndedAt = null
      }
      if (!firstAssistantSpoke) {
        firstAssistantSpoke = true
        fields.firstAssistantAudio = true
      }
      lastAssistantActivityAt = performance.now()
      emit('assistant.speech-start', fields)
    },
    /** Current inter-turn gap (ms since last assistant activity), for drop-relevant lines. */
    gapSinceLastAssistant,
  }
}

export type VoiceLogger = ReturnType<typeof createVoiceLogger>
